import { Box, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from '@mui/material'
import { FeatureFlagIdJml } from 'c1gen/features'
import { C1Alert, C1AlertTitle, C1Button } from 'c1ui'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { useFeatureChecker } from 'components/no-ui/capability'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { appEntitlementId, appEntitlementRefsToSet, appEntitlementSetToRefs } from 'components/pages/entitlement/helpers'
import { useCatalog } from 'components/pages/requestcatalog/details/context/CatalogContext'
import { AddEntitlementsProps } from 'components/pages/requestcatalog/details/setup/add_entitlements/index'
import { Set as ImmutableSet } from 'immutable'
import { AppEntitlement, AppEntitlementCorrelation } from 'pbts/c1/api/app/v1/app_entitlement'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { ListAllEntitlementIdsPerApp } from 'redux_toolkit/request-catalogs'
import { AppEntitlementCorrelationForAccessProfileList } from './list/AppEntitlementCorrelationForAccessProfileList'

export const AddEntitlementsDrawer = ({
    onSubmit,
    close,
}: Pick<AddEntitlementsProps, 'onSubmit'> & ClosableProps): React.ReactElement => {
    const { requestCatalogId } = useCatalog()
    const hasJml = useFeatureChecker(FeatureFlagIdJml)
    const [selectedEntitlements, setSelectedEntitlements] = useState<AppEntitlementCorrelation[]>([])
    const [createRequests, setCreateRequests] = useState(false)

    const {
        data: entitlementRefs,
        isError: isEntitlementRefsError,
        error: entitlementRefsError,
        isLoading: isEntitlementRefsLoading,
    } = ListAllEntitlementIdsPerApp.useListAllEntitlementIdsPerApp(
        {
            catalogId: requestCatalogId,
        },
        {
            evictionAfterSeconds: 0, // evict immediately as we need updated data on every open
        },
    )

    const refsAsAppEntitlementCorrelations = useMemo(
        () => entitlementRefs?.map((ref) => AppEntitlementCorrelation.create({
            appEntitlement: AppEntitlement.create({ appId: ref.appId, id: ref.id }),
            distance: 0,
        })),
        [entitlementRefs],
    )

    const handleShouldProvisionClick = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        setCreateRequests(event.target.checked)
    }, [])

    useEffect(() => {
        if (isEntitlementRefsLoading || isEntitlementRefsError || !refsAsAppEntitlementCorrelations) return
        setSelectedEntitlements(refsAsAppEntitlementCorrelations)
    }, [entitlementRefs, isEntitlementRefsError, isEntitlementRefsLoading, refsAsAppEntitlementCorrelations])

    const onSave = useCallback((): void => {
        if (!entitlementRefs) return

        const selectedEntitlementsAsSet = appEntitlementRefsToSet(selectedEntitlements)
        const previousSelectionsAsSet = ImmutableSet(entitlementRefs.map(appEntitlementId))

        const selectionsToAdd = appEntitlementSetToRefs(selectedEntitlementsAsSet.subtract(previousSelectionsAsSet))
        const selectionsToRemove = appEntitlementSetToRefs(previousSelectionsAsSet.subtract(selectedEntitlementsAsSet))

        onSubmit(selectionsToAdd, selectionsToRemove, createRequests)

        close()
    }, [entitlementRefs, selectedEntitlements, onSubmit, createRequests, close])

    const footerButtons = useMemo(() => <Stack direction="row" spacing={2}>
        <C1Button onClick={close} variant="outlined">
            <FormattedMessage
                id="components_prepare_request_catalog_edit_entitlements_cancel"
                defaultMessage="Cancel"
            />
        </C1Button>
        <C1Button variant="contained" color="primary" type="submit" onClick={onSave}>
            <FormattedMessage
                id="components_prepare_request_catalog_edit_entitlements_save"
                defaultMessage="Save"
            />
        </C1Button>
    </Stack>, [close, onSave])

    return <C1DrawerInner>
        <C1DrawerInner.Header>
            <FormattedMessage defaultMessage="Manage profile entitlements" id="components_request_catalog_entitlements_edited_title" />
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            <Stack spacing={2} sx={{ maxHeight: '100%', flex: 1 }}>
                {entitlementRefsError && <C1Alert severity="error">{entitlementRefsError.message}</C1Alert>}
                <Typography>
                    <FormattedMessage
                        id="components_request_catalog_entitlements_edited_subtitle"
                        defaultMessage="Pick which entitlements you want to add to or remove from your access profile."
                    />
                </Typography>
                {entitlementRefs !== undefined && !isEntitlementRefsLoading && !isEntitlementRefsError && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        overflow: 'auto',
                        maxHeight: hasJml ? 'calc(100vh - 250px)' : 'calc(100vh - 200px)',
                    }}
                    >
                        <AppEntitlementCorrelationForAccessProfileList
                            setSelectedEntitlements={setSelectedEntitlements}
                            selectedEntitlements={selectedEntitlements}
                        />
                    </Box>
                )}
            </Stack>
        </C1DrawerInner.Content>
        <C1DrawerInner.Footer>
            {hasJml
                ? <Stack spacing={1} flex={1}>
                    <C1Alert>
                        <C1AlertTitle>
                            <FormattedMessage
                                defaultMessage="By default, new entitlements will not automatically be granted to currently enrolled users."
                                id="components_prepare_request_catalog_edit_entitlements_provision_info"
                            />
                        </C1AlertTitle>
                    </C1Alert>
                    <Stack spacing={1} justifyContent="space-between" direction="row" alignItems="center">
                        <Box pl={4}>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox
                                        size="small"
                                        onChange={handleShouldProvisionClick}
                                        checked={createRequests}
                                    />}
                                    label={<FormattedMessage
                                        defaultMessage="Create requests for currently enrolled users"
                                        id="components_prepare_request_catalog_edit_entitlements_provision"
                                    />}
                                />
                            </FormGroup>
                        </Box>
                        {footerButtons}
                    </Stack>
                </Stack>
                : footerButtons
            }
        </C1DrawerInner.Footer>
    </C1DrawerInner>
}
